import { useRef } from 'react';

export type DragToScrollCallback = (scrollDelta: { top: number; left: number }) => void;

interface DragScrollOptions {
    distanceMultiplier?: number;
    disabled?: boolean;
}

const useDragScroll = <T extends HTMLElement>(scrollBy: DragToScrollCallback, options?: DragScrollOptions) => {
    const ref = useRef<T>(null);
    const scrollPositionRef = useRef({ top: 0, y: 0, left: 0, x: 0 });
    const distanceMultiplier = options?.distanceMultiplier || 1;
    const disabled = !!options?.disabled;

    const handleMouseUp = (event: MouseEvent) => {
        if (!ref.current || disabled) return;

        event.preventDefault();

        ref.current.style.removeProperty('user-select');

        // Cleanup
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (event: MouseEvent) => {
        if (!ref.current || disabled) return;

        event.preventDefault();

        // How far the mouse has been moved
        const dx = event.clientX - scrollPositionRef.current.x;
        const dy = event.clientY - scrollPositionRef.current.y;

        // Scroll the element
        scrollBy({
            top: scrollPositionRef.current.top - dy * distanceMultiplier,
            left: scrollPositionRef.current.left - dx * distanceMultiplier,
        });

        // Update stored values to prevent comparing old data
        scrollPositionRef.current = {
            top: ref.current.scrollTop,
            left: ref.current.scrollLeft,
            x: event.clientX,
            y: event.clientY,
        };
    };

    const handleMouseDown = (event: React.MouseEvent) => {
        if (!ref.current || disabled) return;

        event.preventDefault();

        // Store scroll values for comparison with mousemove data
        scrollPositionRef.current = {
            top: ref.current.scrollTop,
            left: ref.current.scrollLeft,
            x: event.clientX,
            y: event.clientY,
        };

        // Some styling
        ref.current.style.userSelect = 'none';

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return { ref, onMouseDown: handleMouseDown };
};

export default useDragScroll;
